import React, { useState } from "react";
import { FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

/**
 * Portaled PDF export button. Opens a modal that shows "Building status pack…"
 * progress and flips to a success state when the browser's download fires, so
 * the user has clear confirmation even on slower devices.
 */
export default function PdfExportButton({ onExport, locked, requireActive }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [err, setErr] = useState("");

  const close = () => { setOpen(false); setState("idle"); setErr(""); };

  const run = async () => {
    setOpen(true); setState("running"); setErr("");
    try {
      const fn = requireActive ? requireActive(onExport) : onExport;
      await fn();
      setState("done");
    } catch (e) {
      setErr(e?.message || "Something went wrong building the PDF.");
      setState("error");
      toast.error("PDF export failed");
    }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={locked || state === "running"}
        data-testid="export-pdf-btn"
        className={`inline-flex items-center gap-2 border border-slate-700 ${locked?"opacity-50":"hover:border-blue-500"} text-slate-200 px-3 py-2 rounded-lg text-sm disabled:opacity-50`}
      >
        {state === "running" ? <Loader2 className="animate-spin" size={14}/> : <FileText size={14}/>} PDF
      </button>
      <Modal open={open} onClose={close} testId="pdf-export-modal">
        <ModalHeader icon={FileText} title="PDF status pack" onClose={close} testId="pdf-close-btn"/>
        {state === "running" && (
          <div className="flex items-center gap-3 text-slate-200 py-6" data-testid="pdf-progress">
            <Loader2 className="animate-spin text-blue-400" size={18}/>
            <div>
              <div className="text-sm font-medium">Building your status pack…</div>
              <div className="text-xs text-slate-400 mt-0.5">Rendering Gantt, roll-ups, and the top-3 risks into a shareable PDF.</div>
            </div>
          </div>
        )}
        {state === "done" && (
          <>
            <div className="flex items-center gap-3 py-4" data-testid="pdf-done">
              <CheckCircle2 className="text-emerald-400" size={24}/>
              <div>
                <div className="text-sm text-white font-medium">PDF saved to your downloads</div>
                <div className="text-xs text-slate-400 mt-0.5">Email it to a stakeholder or drop it into a weekly review deck.</div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
              <button onClick={run} data-testid="pdf-rebuild-btn" className="border border-slate-700 hover:border-blue-500 text-slate-200 text-sm px-3 py-2 rounded-lg">Build again</button>
              <button onClick={close} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-lg">Done</button>
            </div>
          </>
        )}
        {state === "error" && (
          <>
            <div className="text-sm text-rose-300 py-4" data-testid="pdf-error">{err}</div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
              <button onClick={close} className="text-sm text-slate-400 hover:text-white px-3 py-2">Close</button>
              <button onClick={run} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-lg">Retry</button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
